import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useConfig } from '../config/ConfigContext';
import apiClient from '../lib/apiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SEOHead from '../components/SEOHead';
import { trackEvent, trackPurchase } from '../components/Analytics';

const CheckoutPage = () => {
  const { cart, dispatch } = useCart();
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    delivery_preferences: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const navigate = useNavigate();
  const { config } = useConfig();

  const getPriceForSize = (product, size) => {
    if (!product?.details?.pricing_variants || !size) {
      return product?.price || 0;
    }
    const variant = product.details.pricing_variants.find(v => v.size === size);
    return variant ? variant.price : product.price;
  };

  useEffect(() => {
    trackEvent('page_view', { page_title: 'Checkout' });
  }, []);

  useEffect(() => {
    if (cart.items.length === 0) {
      navigate('/cart');
      return;
    }

    const fetchProductDetails = async () => {
      try {
        const productIds = [...new Set(cart.items.map(item => item.product_id))];
        const productPromises = productIds.map(id => apiClient.getProduct(id));
        const responses = await Promise.all(productPromises);

        const productsMap = {};
        responses.forEach(response => {
          productsMap[response.id] = response;
        });

        setProducts(productsMap);
      } catch (error) {
        console.error('Error fetching product details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [cart.items, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const total = cart.items.reduce((sum, item) => {
        const product = products[item.product_id];
        const price = getPriceForSize(product, item.size);
        return sum + (price * item.quantity);
      }, 0);

      // Generate temp order ID for WhatsApp message
      const tempOrderId = 'TEMP-' + Date.now().toString(36).toUpperCase();

      // Build order summary for WhatsApp
      let orderMessage = `🛒 *New Order #${tempOrderId}*\n\n`;
      orderMessage += `👤 *Customer Details:*\n`;
      orderMessage += `Name: ${formData.customer_name}\n`;
      orderMessage += `Phone: ${formData.customer_phone}\n`;
      orderMessage += `Email: ${formData.customer_email}\n`;
      orderMessage += `Address: ${formData.customer_address}\n`;
      if (formData.delivery_preferences) {
        orderMessage += `Delivery Notes: ${formData.delivery_preferences}\n`;
      }
      orderMessage += `\n📦 *Order Items:*\n`;

      cart.items.forEach((item, index) => {
        const product = products[item.product_id];
        if (product) {
          const price = getPriceForSize(product, item.size);
          orderMessage += `${index + 1}. ${product.name}`;
          if (item.size) orderMessage += ` (Size: ${item.size})`;
          orderMessage += `\n   Qty: ${item.quantity} × $${price.toFixed(2)} = $${(price * item.quantity).toFixed(2)}\n`;
        }
      });

      orderMessage += `\n💰 *Total Amount: $${total.toFixed(2)}*\n`;
      orderMessage += `💵 Payment Method: Cash on Delivery\n`;

      const rawNumber = config.branding.orderWhatsAppNumber || config.branding.contactPhone;
      let whatsappNumber = rawNumber.replace(/[^0-9]/g, '');
      if (whatsappNumber.startsWith('9610')) {
        whatsappNumber = '961' + whatsappNumber.substring(4);
      }

      const whatsappMessage = encodeURIComponent(orderMessage);
      const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

      // Open WhatsApp FIRST (synchronous, preserves user gesture on mobile)
      const a = document.createElement('a');
      a.href = whatsappLink;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();

      // Then submit order to backend
      const orderData = {
        ...formData,
        items: cart.items.map(item => ({
          product_id: item.product_id,
          quantity: parseInt(item.quantity),
          size: item.size || null
        })),
        total_amount: total
      };

      const response = await apiClient.createOrder(orderData);

      // Track purchase
      trackPurchase(response.id, total, cart.items.map(item => ({
        item_id: item.product_id,
        quantity: item.quantity,
        price: getPriceForSize(products[item.product_id], item.size)
      })));

      // Clear cart
      dispatch({ type: 'CLEAR_CART' });

      // Redirect to success page
      navigate(`/order-success/${response.id}`);
    } catch (error) {
      console.error('Error creating order:', error);
      if (error.response?.status === 422 && error.response.data?.detail) {
        const backendErrors = {};
        error.response.data.detail.forEach(err => {
          const field = err.loc[err.loc.length - 1];
          backendErrors[field] = err.msg || 'Invalid informaton';
        });
        setFormErrors(backendErrors);
        alert('Please fix the errors in the form before submitting.');
      } else {
        alert('Error creating order. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const total = cart.items.reduce((sum, item) => {
    const product = products[item.product_id];
    const price = getPriceForSize(product, item.size);
    return sum + (price * item.quantity);
  }, 0);

  if (loading || !config) {
    return <LoadingSpinner message="Preparing your checkout..." />;
  }

  return (
    <div className="min-h-screen pt-4 md:pt-24 pb-12 bg-gray-50">
      <SEOHead title="Checkout" noindex={true} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-display font-bold text-gray-900 mb-4">Checkout</h1>
          <p className="text-gray-600">Complete your order with cash on delivery</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="bg-white rounded-lg shadow-md p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Delivery Information</h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-blue outline-none transition-all ${formErrors.customer_name ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                      placeholder="Enter your full name"
                    />
                    {formErrors.customer_name && <p className="mt-1 text-xs font-medium text-red-500">{formErrors.customer_name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-blue outline-none transition-all ${formErrors.customer_phone ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                      placeholder="Enter your phone number (minimum 7 digits)"
                    />
                    {formErrors.customer_phone && <p className="mt-1 text-xs font-medium text-red-500">{formErrors.customer_phone}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="customer_email"
                      value={formData.customer_email}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-blue outline-none transition-all ${formErrors.customer_email ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                      placeholder="Enter your email address"
                    />
                    {formErrors.customer_email && <p className="mt-1 text-xs font-medium text-red-500">{formErrors.customer_email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Delivery Address *</label>
                    <textarea
                      name="customer_address"
                      value={formData.customer_address}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-blue outline-none transition-all ${formErrors.customer_address ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                      placeholder="Enter your complete delivery address"
                    />
                    {formErrors.customer_address && <p className="mt-1 text-xs font-medium text-red-500">{formErrors.customer_address}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Delivery Preferences</label>
                    <textarea
                      name="delivery_preferences"
                      value={formData.delivery_preferences}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                      placeholder="Any special instructions for delivery..."
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-brand-blue hover:bg-brand-red text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
              >
                {submitting ? (
                  <>
                    <span className="inline-block animate-spin mr-3">⏳</span>
                    Placing Order...
                  </>
                ) : (
                  'Complete Order & Send to WhatsApp'
                )}
              </button>
            </form>
          </div>

          <div>
            <div className="bg-white rounded-lg shadow-md p-8 sticky top-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>

              <div className="space-y-4 mb-8">
                {cart.items.map((item, index) => {
                  const product = products[item.product_id];
                  if (!product) return null;

                  return (
                    <div key={`${item.product_id}-${item.size}-${index}`} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl">
                      <img
                        src={(product.images && product.images[product.thumbnail_index || 0]) || '/api/placeholder/60/60'}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-sm">{product.name}</h3>
                        {item.size && <p className="text-xs text-gray-600">Size: {item.size}</p>}
                        <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
                      </div>
                      <span className="font-bold text-brand-blue">${(getPriceForSize(product, item.size) * item.quantity).toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-gray-200 pt-6 space-y-3">
                <div className="flex justify-between text-lg">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-semibold">${total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span className="text-gray-600">Shipping:</span>
                  <span className="font-semibold text-green-600">FREE</span>
                </div>
                <div className="flex justify-between text-xl font-bold border-t pt-3">
                  <span>Total:</span>
                  <span className="text-brand-blue">${total.toFixed(2)}</span>
                </div>
              </div>

              <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-start space-x-3">
                  <svg className="w-6 h-6 text-brand-blue mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-brand-blue">Cash on Delivery</p>
                    <p className="text-xs text-brand-blue mt-1">
                      Pay when your order is delivered to your door. No online payment required.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
